
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Calendar, User, DollarSign, Hash } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ScheduledCoupon } from "@/types/scheduled-coupons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ErrorDetailsDialogProps {
  coupon: ScheduledCoupon;
}

export const ErrorDetailsDialog: React.FC<ErrorDetailsDialogProps> = ({ coupon }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'error':
        return 'Erro';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <AlertCircle className="h-4 w-4 mr-1" />
          Ver Erro
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Detalhes do Erro - Cupom {coupon.id.slice(-8)}
          </DialogTitle>
          <DialogDescription>
            Informações detalhadas sobre o erro ocorrido durante o processamento deste cupom.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Status */}
          <div className="flex items-center gap-3">
            <Badge className={getStatusColor(coupon.status)}>
              {getStatusLabel(coupon.status)}
            </Badge>
            {coupon.reversed_at && (
              <span className="text-sm text-gray-500">
                em {format(new Date(coupon.reversed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </span>
            )}
          </div>

          {/* Motivo do Erro */}
          {coupon.reversal_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-800 mb-2">Motivo do Erro:</h4>
              <p className="text-red-700 whitespace-pre-wrap">{coupon.reversal_reason}</p>
            </div>
          )}

          {/* Informações do Cliente */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              Informações do Cliente
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600">Nome:</span>
                <span className="ml-2 font-medium">{coupon.customer_name || 'Não informado'}</span>
              </div>
              <div>
                <span className="text-gray-600">Email:</span>
                <span className="ml-2 font-medium">{coupon.customer_email}</span>
              </div>
              <div>
                <span className="text-gray-600">ID do Pedido:</span>
                <span className="ml-2 font-mono text-xs bg-gray-200 px-2 py-1 rounded">
                  {coupon.reference_order_id}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Valor Gasto:</span>
                <span className="ml-2 font-medium">
                  R$ {coupon.total_spent_amount?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Informações do Cupom */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Valores Calculados
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-blue-600">Valor do Cupom:</span>
                <span className="ml-2 font-medium">
                  {coupon.coupon_value ? `R$ ${coupon.coupon_value.toFixed(2)}` : 'Não calculado'}
                </span>
              </div>
              <div>
                <span className="text-blue-600">Valor Mínimo:</span>
                <span className="ml-2 font-medium">
                  {coupon.minimum_purchase_amount ? `R$ ${coupon.minimum_purchase_amount.toFixed(2)}` : 'Não calculado'}
                </span>
              </div>
              <div>
                <span className="text-blue-600">Percentual:</span>
                <span className="ml-2 font-medium">
                  {coupon.cashback_percentage ? `${coupon.cashback_percentage}%` : 'Não definido'}
                </span>
              </div>
              <div>
                <span className="text-blue-600">Multiplicador:</span>
                <span className="ml-2 font-medium">
                  {coupon.cashback_multiplier ? `${coupon.cashback_multiplier}x` : 'Não definido'}
                </span>
              </div>
            </div>
          </div>

          {/* Agendamento */}
          {coupon.scheduled_for && (
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Agendamento
              </h4>
              <p className="text-purple-700">
                Agendado para: {format(new Date(coupon.scheduled_for), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
            </div>
          )}

          {/* ID do Cupom */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <Hash className="h-4 w-4" />
              Identificadores
            </h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">ID do Cupom:</span>
                <span className="ml-2 font-mono text-xs bg-gray-200 px-2 py-1 rounded">
                  {coupon.id}
                </span>
              </div>
              {coupon.external_coupon_id && (
                <div>
                  <span className="text-gray-600">ID Externo:</span>
                  <span className="ml-2 font-mono text-xs bg-gray-200 px-2 py-1 rounded">
                    {coupon.external_coupon_id}
                  </span>
                </div>
              )}
              {coupon.campaign_id && (
                <div>
                  <span className="text-gray-600">ID da Campanha:</span>
                  <span className="ml-2 font-mono text-xs bg-gray-200 px-2 py-1 rounded">
                    {coupon.campaign_id}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Histórico */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2">Histórico</h4>
            <div className="space-y-1 text-sm">
              <div>
                <span className="text-gray-600">Criado em:</span>
                <span className="ml-2">
                  {format(new Date(coupon.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Última atualização:</span>
                <span className="ml-2">
                  {format(new Date(coupon.updated_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
